import { strCompareLowerCase } from '@common/Common';
import GoalNavigation from "@common/Components/GoalNavigation.vue";
import PortfolioNavigation from "@common/Components/PortfolioNavigation.vue";
import { ICommandStatus } from '@cqrs/cqrs';
import { ICriteriaCommand } from '@Criteria/cqrs';
import { AttributeLibraryViewModel } from '@Criteria/Hierarchies/Components/AttributeLibrary';
import AttributeLibrary from '@Criteria/Hierarchies/Components/AttributeLibrary.vue';
import { CriteriaFromStringBuilderViewModel } from '@Criteria/Hierarchies/Components/CriteriaFromStringBuilder';
import CriteriaFromStringBuilder from '@Criteria/Hierarchies/Components/CriteriaFromStringBuilder.vue';
import { CriteriaLibraryViewModel } from '@Criteria/Hierarchies/Components/CriteriaLibrary';
import CriteriaLibrary from '@Criteria/Hierarchies/Components/CriteriaLibrary.vue';
import { CriterionCreatorViewModel } from '@Criteria/Hierarchies/Components/CriterionCreator';
import CriterionCreator from '@Criteria/Hierarchies/Components/CriterionCreator.vue';
import { CriterionDeleterViewModel } from '@Criteria/Hierarchies/Components/CriterionDeleter';
import CriterionDeleter from '@Criteria/Hierarchies/Components/CriterionDeleter.vue';
import { CriterionEditorViewModel } from '@Criteria/Hierarchies/Components/CriterionEditor';
import CriterionEditor from '@Criteria/Hierarchies/Components/CriterionEditor.vue';
import { ChangeCriterionParentCommandV1, CreateCriteriaFromStringCommandV1, CreateCriterionCommandV1, CreateCriterionFromAttributeCommandV1, CreateCriterionFromTemplateCommandV1, DeleteCriterionCommandV1, UpdateCriterionCommandV1 } from '@Criteria/Hierarchies/Domain/commands-core';
import CriteriaHierarchyCommands from "@Criteria/Hierarchies/Domain/commands-vue-component";
import CriteriaHierarchyQueries from "@Criteria/Hierarchies/Domain/queries-vue-component";
import { AttributeLibraryReadModel, CriteriaHierarchyReadModel, CriteriaLibraryReadModel, CriteriaPageReadModel, CriterionDependenciesReadModel, CriterionReadModel, SubcriteriaReadModel } from "@Criteria/Hierarchies/Domain/readmodels";
import { ScaleTemplateViewerViewModel } from '@Scales/Components/ScaleTemplateViewer';
import ScaleTemplateViewer from '@Scales/Components/ScaleTemplateViewer.vue';
import { DiscreteScale } from '@Scales/Model';
import Component, { mixins } from 'vue-class-component';


@Component({
    components: {
        GoalNavigation, PortfolioNavigation, CriteriaLibrary, ScaleTemplateViewer, CriteriaFromStringBuilder,
        AttributeLibrary, CriterionEditor, CriterionCreator, CriterionDeleter
    }
})
export default class CriteriaPage extends mixins(CriteriaHierarchyQueries, CriteriaHierarchyCommands)
{
    get workspaceId() { return Number.parseInt(this.$route.params.workspaceId); }
    get projectId() { return Number.parseInt(this.$route.params.projectId); }
    get evalModelId() { return Number.parseInt(this.$route.params.evalModelId); }

    scaleTemplateViewer = new ScaleTemplateViewerViewModel();
    criteriaFromStringBuilder = new CriteriaFromStringBuilderViewModel();
    attributeLibrary = new AttributeLibraryViewModel();
    criterionEditor = new CriterionEditorViewModel();
    criterionCreator = new CriterionCreatorViewModel();
    criterionDeleter = new CriterionDeleterViewModel();
    criteriaLibrary = new CriteriaLibraryViewModel();


    Initialized: boolean = false;
    Criteria: CriterionViewModel[] = [];
    CriteriaHierarchyId: number;
    ScoringEnabled: boolean = null;

    get goal(): CriterionViewModel { return this.Criteria?.find(a => a.ParentId == null); }
    get goalName(): string { return this.goal?.Name; }

    get criteriaView(): CriterionViewModel[]
    {
        const collapsed = this.Criteria.filter(criterion => criterion.IsCollapsed);

        if (collapsed.length === 0)
        {
            return this.Criteria.sort((a, b) => strCompareLowerCase(a.Path, b.Path));
        }

        return this.Criteria.filter(descendant =>
        {
            // A criterion with no parent is always visible (e.g., the root).
            if (descendant.ParentId == null) return true;

            // Check if any of the collapsed items is an ancestor of the current criterion.
            for (const collapsedItem of collapsed)
            {
                if (this.isDescendant(collapsedItem, descendant))
                {
                    return false; // A collapsed ancestor found, so this criterion is not visible.
                }
            }
            return true; // No collapsed ancestors, so this criterion is visible.
        }).sort((a, b) => strCompareLowerCase(a.Path, b.Path));
    }


    isGoal(criterionId: number): boolean { return this.goal.CriterionId === criterionId; }

    isDescendant(ancestor: CriterionViewModel, descendant: CriterionViewModel): boolean
    {
        // Ensure the ancestor path is not equal to the descendant path (to avoid considering an item as its own ancestor)
        // and check if the descendant path starts with the ancestor path
        return descendant.Path !== ancestor.Path && descendant.Path.startsWith(ancestor.Path);
    }

    hasSubCriteria(criterionId: number): boolean
    {
        return this.Criteria.some(a => a.ParentId === criterionId);
    }

    isDragEnabled(criterionId: number): boolean { return this.goal.CriterionId !== criterionId; }


    showCriterionEditor(vm: CriterionViewModel)
    {
        this.criterionEditor.show(vm.CriterionId, vm.Name, vm.Description, this.goal.CriterionId === vm.CriterionId);
    }

    showCriterionCreator(parent: CriterionViewModel)
    {
        this.criterionCreator.show(parent.CriterionId, parent.Name);
    }

    showCriteriaFromStringBuilder(parent: CriterionViewModel)
    {
        this.criteriaFromStringBuilder.show(parent.CriterionId, parent.Name);
    }

    showCriteriaLibrary(parent: CriterionViewModel)
    {
        this.criteriaLibrary.show(parent.CriterionId);

        if (!this.criteriaLibrary.data)
        {
            this.runCriteriaLibraryQueryV1(this.CriteriaHierarchyId, this.workspaceId);
        }
    }

    showAttributeLibrary(parent: CriterionViewModel)
    {
        this.attributeLibrary.show(parent.CriterionId);
        this.runAttributeLibraryQueryV1(this.CriteriaHierarchyId, this.workspaceId);
    }

    showCriterionDeleter(criterionId: number)
    {
        this.criterionDeleter.show(criterionId);
        this.runCriterionDependenciesQueryV1(this.workspaceId, this.CriteriaHierarchyId, criterionId);
    }

    showScaleTemplateViewer(scale: DiscreteScale)
    {
        this.scaleTemplateViewer.show(scale);
    }

    updateCriterion(rm: CriterionReadModel)
    {
        const vm = this.Criteria.find(a => a.CriterionId === rm.CriterionId);
        vm.update(rm);
    }


    onDragOver(event: DragEvent)
    {
        event.preventDefault(); // Prevent the default dragover behavior to allow dropping.

        const threshold = 100; // Pixels from the viewport's top and bottom edges where scrolling should start
        const maxScrollStep = 20; // Maximum pixels to scroll per event

        // Get the cursor's Y position relative to the viewport
        const cursorYRelativeToViewport = event.clientY;

        // Calculate the distance from the cursor to the top and bottom of the viewport
        const distanceFromTop = cursorYRelativeToViewport;
        const distanceFromBottom = window.innerHeight - cursorYRelativeToViewport;

        // Decide whether to scroll up or down based on the cursor's position
        if (distanceFromTop < threshold)
        {
            // Scroll up
            window.scrollBy(0, -maxScrollStep);
        } else if (distanceFromBottom < threshold)
        {
            // Scroll down
            window.scrollBy(0, maxScrollStep);
        }
    }

    onDragStart(event: DragEvent, criterion: CriterionViewModel)
    {
        if (this.isGoal(criterion.CriterionId))
        {
            event.preventDefault();
        }
        else
        {
            event.dataTransfer.setData('application/json', criterion.CriterionId.toString());
        }
    }

    onDragEnd(event: DragEvent, vm: CriterionViewModel)
    {
        vm.DragOver = false;
    }

    onDragEnter(event: DragEvent, vm: CriterionViewModel)
    {
        vm.DragOver = true;
        event.preventDefault();
    }

    onDragLeave(event: DragEvent, vm: CriterionViewModel)
    {
        //if (!event.currentTarget.contains(event.relatedTarget))
        //{
        //    vm.DragOver = false;
        //}

        const currentTarget = event.currentTarget as HTMLElement;

        if (currentTarget && !currentTarget.contains(event.relatedTarget as Node))
        {
            vm.DragOver = false;
        }
    }

    onDrop(event: DragEvent, target: CriterionViewModel)
    {
        target.DragOver = false;

        const droppedId = Number(event.dataTransfer.getData('application/json'));
        const dropped = this.Criteria.find(a => a.CriterionId === droppedId);

        if (droppedId === target.CriterionId)
        {
            return;
        }

        if (dropped.ParentId === target.CriterionId)
        {
            return;
        }

        if (this.isDescendant(dropped, target))
        {
            return;
        }

        const prevParent = this.Criteria.find(a => a.CriterionId === dropped.ParentId);

        if (confirm(`Judgements related with ${dropped.Name} in the context of ${prevParent.Name} will be lost!\r\nAre your sure to move this criterion?`))
        {
            this.handleChangeCriterionParentCommand(dropped.CriterionId, target.CriterionId);
        }
    }



    handleCreateCriterionCommand(parentId: number, name: string, description: string | null)
    {
        this.invokeCreateCriterionCommandV1(this.CriteriaHierarchyId, parentId, name, description, this.workspaceId);
    }

    handleDeleteCriterionCommand(criterionId: number)
    {
        this.invokeDeleteCriterionCommandV1(this.CriteriaHierarchyId, criterionId, this.workspaceId);
    }

    handleUpdateCriterionCommand(criterionId: number, name: string, description: string)
    {
        this.invokeUpdateCriterionCommandV1(this.CriteriaHierarchyId, criterionId, name, description, this.workspaceId);
    }

    handleCreateCriterionFromTemplateCommand(parentId: number, attributeId: number, name: string)
    {
        this.invokeCreateCriterionFromTemplateCommandV1(this.CriteriaHierarchyId, parentId, name, attributeId, this.workspaceId);
    }

    handleChangeCriterionParentCommand(criterionId: number, parentId: number)
    {
        this.invokeChangeCriterionParentCommandV1(this.CriteriaHierarchyId, criterionId, parentId, this.workspaceId);
    }

    handleCreateCriteriaFromStringCommand(parentId: number, val: string)
    {
        this.invokeCreateCriteriaFromStringCommandV1(this.CriteriaHierarchyId, parentId, val, this.workspaceId);
    }

    handleCreateCriterionFromAttributeCommand(parentId: number, attributeId: number, name: string)
    {
        this.invokeCreateCriterionFromAttributeCommandV1(this.CriteriaHierarchyId, parentId, attributeId, name, this.workspaceId)
    }


    mounted()
    {
        document.title = "Criteria | TransparentChoice";
        this.runCriteriaPageQueryV1(this.workspaceId, this.evalModelId);
    }

    onSetModel(model: any, modelName: string)
    {
        if (modelName == "CriteriaPageReadModel")
        {
            const rm = model as CriteriaPageReadModel;

            this.CriteriaHierarchyId = rm.CriteriaHierarchyId;
            this.ScoringEnabled = rm.ScoringEnabled;
            this.Criteria = rm.Criteria.map(a => new CriterionViewModel(a));
            this.Initialized = true;
        }
        else if (modelName == "CriteriaHierarchyReadModel")
        {
            const rm = model as CriteriaHierarchyReadModel;
            rm.Criteria.forEach(c => this.updateCriterion(c));
        }
        else if (modelName == "CriterionReadModel")
        {
            const rm = model as CriterionReadModel;
            this.updateCriterion(rm);
        }
        else if (modelName == "SubcriteriaReadModel")
        {
            const rm = model as SubcriteriaReadModel;
            const names = rm.Subcriteria.map(a => a.Name);

            for (const sub of rm.Subcriteria)
            {
                if (!this.Criteria.some(a => a.CriterionId === sub.CriterionId))
                {
                    this.Criteria.push(new CriterionViewModel(sub));
                }
            }

            this.attributeLibrary.setUsed(names);
            this.criteriaLibrary.setUsed(names);
        }
        else if (modelName == "CriteriaLibraryReadModel")
        {
            const rm = model as CriteriaLibraryReadModel;
            this.criteriaLibrary.setData(rm);
        }
        else if (modelName == "AttributeLibraryReadModel")
        {
            const rm = model as AttributeLibraryReadModel;
            this.attributeLibrary.setData(rm);
        }
        else if (modelName == "CriterionDependenciesReadModel")
        {
            const rm = model as CriterionDependenciesReadModel;
            this.criterionDeleter.setData(rm);
        }
    }

    onCommandSucceed(cmd: ICriteriaCommand, correlationId: string)
    {
        if (DeleteCriterionCommandV1.matchType(cmd))
        {
            const criterionId = Number.parseInt(correlationId);
            const criterionToDelete = this.Criteria.find(c => c.CriterionId === criterionId);

            if (criterionToDelete)
            {
                // Explicitly check for both the criterion itself and its descendants in the filter condition
                this.Criteria = this.Criteria.filter(c =>
                {
                    // Check if c is not the criterion to delete and also not a descendant of the criterion to delete
                    return c.CriterionId !== criterionId && !this.isDescendant(criterionToDelete, c);
                });                
            }

            this.criterionEditor.close();
            this.criterionDeleter.close();
        }
        else if (ChangeCriterionParentCommandV1.matchType(cmd))
        {
            this.runCriteriaHierarchyQueryV1(this.CriteriaHierarchyId, this.workspaceId);
        }
        else if (UpdateCriterionCommandV1.matchType(cmd))
        {
            //changing name is changing paths of subcriteria
            this.runCriteriaHierarchyQueryV1(this.CriteriaHierarchyId, this.workspaceId);
            this.criterionEditor.close();
        }
        else if (CreateCriteriaFromStringCommandV1.matchType(cmd))
        {
            const parentId = Number.parseInt(correlationId);
            this.runSubcriteriaQueryV1(this.CriteriaHierarchyId, parentId, this.workspaceId);
            this.criteriaFromStringBuilder.close();
        }
        else if (CreateCriterionCommandV1.matchType(cmd))
        {
            const parentId = Number.parseInt(correlationId);
            this.runSubcriteriaQueryV1(this.CriteriaHierarchyId, parentId, this.workspaceId);
            this.criterionCreator.reset();
        }
        else if (CreateCriterionFromAttributeCommandV1.matchType(cmd))
        {
            const parentId = Number.parseInt(correlationId);
            this.runSubcriteriaQueryV1(this.CriteriaHierarchyId, parentId, this.workspaceId);
        }
        else if (CreateCriterionFromTemplateCommandV1.matchType(cmd))
        {
            const parentId = Number.parseInt(correlationId);
            this.runSubcriteriaQueryV1(this.CriteriaHierarchyId, parentId, this.workspaceId);
        }
    }

    onCommandFailed(status: ICommandStatus)
    {
        if (CreateCriterionCommandV1.matchType(status.Command))
        {
            this.criterionCreator.displayValidationErrors(status.ValidationErrors);
        }
        else if (UpdateCriterionCommandV1.matchType(status.Command))
        {
            this.criterionEditor.displayValidationErrors(status.ValidationErrors);
        }
    }
}

class CriterionViewModel
{
    rm: CriterionReadModel = null;

    get CriterionId(): number { return this.rm.CriterionId }
    get ParentId(): number | null { return this.rm.ParentId }
    get Name(): string { return this.rm.Name }
    get Description(): string { return this.rm.Description }
    get Path(): string { return this.rm.Path }
    get Level(): number | null { return this.rm.Level }

    IsCollapsed: boolean = null;
    Dragged: boolean = false;
    DragOver: boolean = false;

    constructor(rm: CriterionReadModel)
    {
        this.rm = rm;
    }

    update(rm: CriterionReadModel)
    {
        this.rm = rm;
    }
}